import { closestAcrossShadowBoundaries } from "@/runtime/shared/shadow-dom";
import type { TypeaheadItem, UseTypeaheadReturn } from "composables/useTypeahead";

/**
 * Check if an e-menu-item host is navigable (not disabled, not a label).
 */
const isNavigable = (el: Element): el is HTMLElement => {
  return el.localName === "e-menu-item" && el.getAttribute("type") !== "label" && !el.hasAttribute("disabled");
};

/**
 * Walk up through shadow boundaries to find the containing `e-menu-item` host.
 */
export const findMenuItemHost = (el: Element | null): HTMLElement | null => {
  return closestAcrossShadowBoundaries(el, "e-menu-item") as HTMLElement | null;
};

/**
 * Return the focusable element (`<a>` or `<summary>`) inside an e-menu-item's shadow root.
 * Returns null for label or disabled items.
 */
export const getFocusableElement = (host: HTMLElement): HTMLElement | null => {
  if (!isNavigable(host)) return null;
  const root = host.shadowRoot;
  if (!root) return null;
  // Non-expandable items render <a>; expandable items render <details><summary>
  return (root.querySelector("a") ?? root.querySelector("summary")) as HTMLElement | null;
};

/**
 * Collect navigable top-level `e-menu-item` elements from the menu's default slot.
 */
export const getTopLevelItems = (menuHost: Element): HTMLElement[] => {
  const root = menuHost.shadowRoot;
  if (!root) return [];
  const slot = root.querySelector(".e-menu-list > slot") as HTMLSlotElement | null;
  if (!slot) return [];
  return (slot.assignedElements({ flatten: true }) as HTMLElement[]).filter(isNavigable);
};

/**
 * Collect navigable child `e-menu-item` elements from an expandable item's children slot.
 */
export const getSubItems = (itemHost: Element): HTMLElement[] => {
  const root = itemHost.shadowRoot;
  if (!root) return [];
  const slot = root.querySelector('slot[name="children"]') as HTMLSlotElement | null;
  if (!slot) return [];
  return (slot.assignedElements({ flatten: true }) as HTMLElement[]).filter(isNavigable);
};

/**
 * Map `e-menu-item` host elements to `TypeaheadItem[]` for use with `useTypeahead`.
 */
export const buildTypeaheadItems = (items: HTMLElement[]): TypeaheadItem[] => {
  return items.map((host) => ({
    value: host,
    label: host.getAttribute("label") || host.textContent?.trim() || "",
    disabled: host.hasAttribute("disabled"),
    element: host,
  }));
};

/**
 * Move focus to the item at `offset` positions from the current item, with wrapping.
 */
export const focusItemAtOffset = (
  items: HTMLElement[],
  currentHost: HTMLElement | null,
  offset: number,
): HTMLElement | null => {
  if (items.length === 0) return null;
  const currentIndex = currentHost ? items.indexOf(currentHost) : -1;
  let nextIndex: number;
  if (currentIndex === -1) {
    nextIndex = offset > 0 ? 0 : items.length - 1;
  } else {
    nextIndex = (currentIndex + offset + items.length) % items.length;
  }
  const focusable = getFocusableElement(items[nextIndex]);
  if (focusable) {
    focusable.focus();
    return items[nextIndex];
  }
  return null;
};

/**
 * Focus the first navigable item in the list.
 */
export const focusFirst = (items: HTMLElement[]): HTMLElement | null => {
  if (items.length === 0) return null;
  const focusable = getFocusableElement(items[0]);
  if (focusable) {
    focusable.focus();
    return items[0];
  }
  return null;
};

/**
 * Focus the last navigable item in the list.
 */
export const focusLast = (items: HTMLElement[]): HTMLElement | null => {
  const last = items.at(-1);
  if (!last) return null;
  const focusable = getFocusableElement(last);
  if (focusable) {
    focusable.focus();
    return last;
  }
  return null;
};

/** Whether a key is a single printable character (not a modifier combo). */
const isPrintableKey = (event: KeyboardEvent): boolean => {
  return event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey;
};

/**
 * Handle ArrowUp/Down, Home/End, and typeahead for a list of menu items.
 * Returns `true` if the event was consumed.
 */
export const handleListNavigation = (
  event: KeyboardEvent,
  items: HTMLElement[],
  focusedHost: HTMLElement,
  typeahead: UseTypeaheadReturn,
): boolean => {
  switch (event.key) {
    case "ArrowDown": {
      event.preventDefault();
      focusItemAtOffset(items, focusedHost, 1);
      return true;
    }
    case "ArrowUp": {
      event.preventDefault();
      focusItemAtOffset(items, focusedHost, -1);
      return true;
    }
    case "Home": {
      event.preventDefault();
      focusFirst(items);
      return true;
    }
    case "End": {
      event.preventDefault();
      focusLast(items);
      return true;
    }
    default: {
      if (isPrintableKey(event)) {
        const typeaheadItems = buildTypeaheadItems(items);
        const match = typeahead.handleTypeaheadSearch(event.key, typeaheadItems, focusedHost);
        if (match?.element) {
          const focusable = getFocusableElement(match.element);
          if (focusable) focusable.focus();
        }
        return true;
      }
      return false;
    }
  }
};

export { getDeepActiveElement } from "@/runtime/shared/shadow-dom";
